import { Suspense, lazy, useMemo } from "react";
import { Routes, Route } from "react-router-dom";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getRequest } from "../apiService";

// Layout components
import TopBar_2 from "./menu/TopBar";
import SideBar_2 from "./menu/SideBar";
import Modal_pg from "../app/Layouts/Modal_pg";
import Default_pg from "../app/Layouts/Default_pg";
import TopLocked_scroll_pg from "../app/Layouts/TopLocked_scroll_pg";
import Banner_Notification from "./UI/Banner_Notification";
import ErrorBoundary_component from "../ErrorHandling/ErrorBoundary_component";

// System pages
import {Home} from "../pages/Home";
import NotFound from "../pages/NotFound";

// Vite glob imports - these are resolved at build time
// Using eager: false means they'll be code-split and lazy loaded
// Negative patterns (starting with !) exclude files
const allModules = import.meta.glob([
  '../pages/**/*.{js,jsx,ts,tsx}',
  '../components/**/*.{js,jsx,ts,tsx}',
  '../app/**/*.{js,jsx,ts,tsx}',
  '!**/*.test.{js,jsx,ts,tsx}',
  '!**/*.spec.{js,jsx,ts,tsx}',
  '!**/tests/**'
]);

console.log('Available modules for dynamic import:', Object.keys(allModules));

function AppRouter() {

  const [menuData, setMenuData] = useState({ pages: [], dynamicRoutes: [] });
  const [menuSections, setMenuSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const bannerVisibility = useSelector((state) => state.bannerHandler.visibility);

  useEffect(() => {
    initializeMenuData();
  }, []);

  const initializeMenuData = async () => {
    try {
      // Fetch pages
      const pagesResult = await getRequest("menu/getPages");
      
      // Fetch sections
      const sectionsResult = await getRequest("menu/getSections");
      
      if (pagesResult.success && sectionsResult.success) {
        setMenuData(pagesResult.data.data);
        setMenuSections(sectionsResult.data.data);
      } else {
        console.error("Failed to load menu data:", {
          pages: pagesResult.error,
          sections: sectionsResult.error
        });
        setMenuData({ pages: [], dynamicRoutes: [] });
        setMenuSections([]);
      }
    } catch (error) {
      console.error("Error loading menu:", error);
      setMenuData({ pages: [], dynamicRoutes: [] });
      setMenuSections([]);
    } finally {
      setLoading(false);
    }
  };

  // Create dynamic component loaders based on database configuration
  const dynamicComponents = useMemo(() => {
    const components = {};
    
    menuData.pages.forEach(page => {
      if (page.componentPath) {
        try {
         let moduleLoader = null;
          let resolvedPath = '';
          
          // If path already starts with ../ or ./, use it as-is
          if (page.componentPath.startsWith('../') || page.componentPath.startsWith('./')) {
            const base = page.componentPath;
            moduleLoader = allModules[`${base}.jsx`] || allModules[`${base}.js`]
              || allModules[`${base}.tsx`] || allModules[`${base}.ts`];
            resolvedPath = `${base}.jsx`;
          } else {
            // Try with ../ prefix first (for pages outside components folder)
            const base = `../${page.componentPath}`;
            moduleLoader = allModules[`${base}.jsx`] || allModules[`${base}.js`]
              || allModules[`${base}.tsx`] || allModules[`${base}.ts`];
            resolvedPath = `${base}.jsx`;

            // If not found with ../, try with ./ (for components in same folder)
            if (!moduleLoader) {
              const localBase = `./${page.componentPath}`;
              moduleLoader = allModules[`${localBase}.jsx`] || allModules[`${localBase}.js`]
                || allModules[`${localBase}.tsx`] || allModules[`${localBase}.ts`];
              if (moduleLoader) resolvedPath = `${localBase}.jsx`;
            }
          }
          
          if (!moduleLoader) {
            console.error(`Module not found: ${resolvedPath || page.componentPath}`);
            
            // Create error component
            components[page.id] = () => (
              <div style={{ padding: '20px', border: '2px solid #f44336', borderRadius: '8px', margin: '20px' }}>
                <h3 style={{ color: '#f44336' }}>⚠️ Component Not Found</h3>
                <p><strong>Page:</strong> {page.pgName}</p>
                <p><strong>Expected Path:</strong> {resolvedPath || page.componentPath}</p>
                <p><strong>Component Type:</strong> {page.componentType || 'default'}</p>
                {page.exportName && <p><strong>Export Name:</strong> {page.exportName}</p>}
                <details style={{ marginTop: '10px' }}>
                  <summary style={{ cursor: 'pointer' }}>Available Modules</summary>
                  <pre style={{ fontSize: '11px', overflow: 'auto' }}>
                    {Object.keys(allModules).join('\n')}
                  </pre>
                </details>
              </div>
            );
            return;
          }

          // Handle different export types
          const componentLoader = () => {
            return moduleLoader().then(module => {
              
              switch (page.componentType) {
                case 'named_export':
                case 'multiple_exports':
                  if (!module[page.exportName]) {
                    console.error(`Export "${page.exportName}" not found in ${page.componentPath}`);
                    throw new Error(`Export "${page.exportName}" not found`);
                  }
                  return { default: module[page.exportName] };
                
                default:
                  // Default export
                  return module;
              }
            }).catch(err => {
              console.error(`✗ Failed to load ${fullPath}:`, err);
              return { 
                default: () => (
                  <div style={{ padding: '20px', border: '2px solid #f44336', borderRadius: '8px', margin: '20px' }}>
                    <h3 style={{ color: '#f44336' }}>⚠️ Error Loading Component</h3>
                    <p><strong>Page:</strong> {page.pgName}</p>
                    <p><strong>Path:</strong> {fullPath}</p>
                    <p><strong>Error:</strong> {err.message}</p>
                  </div>
                )
              };
            });
          };
          
          components[page.id] = lazy(componentLoader);
          
        } catch (error) {
          console.error(`Error setting up component for ${page.pgName}:`, error);
          components[page.id] = () => (
            <div style={{ padding: '20px', border: '2px solid #ff9800', borderRadius: '8px', margin: '20px' }}>
              <h3 style={{ color: '#ff9800' }}>⚠️ Configuration Error</h3>
              <p><strong>Page:</strong> {page.pgName}</p>
              <p><strong>Component Path:</strong> {page.componentPath}</p>
              <p><strong>Error:</strong> {error.message}</p>
            </div>
          );
        }
      }
    });
    
    return components;
  }, [menuData.pages]);

  const renderComponent = (page) => {
    const Component = dynamicComponents[page.id];
    
    if (!Component) {
      console.error('NO COMPONENT FOUND for:', page.pgName, page.id);
      return (
        <div style={{ padding: '20px' }}>
          <h3>⚠️ Component Not Configured</h3>
          <p><strong>Page:</strong> {page.pgName}</p>
          <p><strong>Path:</strong> {page.urlPath}</p>
        </div>
      );
    }

    // Parse props
    const requiredProps = {};
    if (page.requiredProps) {
      try {
        const required = JSON.parse(page.requiredProps);
        required.forEach(prop => {
          if (prop === 'title') requiredProps.title = page.pgName;
        });
      } catch (e) {
        console.warn(`Invalid requiredProps JSON for page ${page.id}`);
      }
    }

    return (
      <ErrorBoundary_component
        fallback={
          <div className="err_default">
            <p><b>Error:</b> Failed to render {page.pgName}</p>
          </div>
        }
      >
        <Suspense fallback={<div>Loading {page.pgName}...</div>}>
          <Component {...requiredProps} />
        </Suspense>
      </ErrorBoundary_component>
    );
  };

  // Layout renderer based on database Layout field
  const renderWithLayout = (page) => {
    const component = renderComponent(page);
    
    const layoutProps = {
      title: page.pgName,
      id: page.id,
      ...(page.Align_y && { Align_vert: page.Align_y }),
      ...(page.AlignmentName && { Align_vert: page.AlignmentName })
    };

    // Handle special configurations from database
    if (page.specialConfigType === 'modal_with_actions' && page.specialConfigData) {
      try {
        const specialConfig = typeof page.specialConfigData === 'string' 
          ? JSON.parse(page.specialConfigData) 
          : page.specialConfigData;
        Object.assign(layoutProps, specialConfig);
      } catch (e) {
        console.warn(`Invalid specialConfigData for page ${page.id}:`, page.specialConfigData);
      }
    }

    // Use both possible column names for Layout
    const layoutType = page.Layout || page.LayoutName;

    switch (layoutType) {
      case "Modal_pg":
        return (
          <Route
            key={`layout-${page.id}`}
            element={<Modal_pg {...layoutProps} />}
          >
            <Route path={page.urlPath} element={component} />
          </Route>
        );
        
      case "Default_pg":
        return (
          <Route
            key={`layout-${page.id}`}
            element={<Default_pg {...layoutProps} />}
          >
            <Route path={page.urlPath} element={component} />
          </Route>
        );
        
      case "TopLocked_scroll_pg":
        return (
          <Route
            key={`layout-${page.id}`}
            element={<TopLocked_scroll_pg {...layoutProps} />}
          >
            <Route path={page.urlPath} element={component} />
          </Route>
        );
        
      default:
        // No layout wrapper
        return (
          <Route
            key={page.id}
            path={page.urlPath}
            element={component}
          />
        );
    }
  };

// Render dynamic routes from database
  const renderDynamicRoutes = () => {
    return menuData.dynamicRoutes.map(route => {
      let Component;
      
      if (route.componentPath) {
        let moduleLoader = null;
        
        // If path already starts with ../ or ./, use it as-is
        if (route.componentPath.startsWith('../') || route.componentPath.startsWith('./')) {
          const base = route.componentPath;
          moduleLoader = allModules[`${base}.jsx`] || allModules[`${base}.js`]
            || allModules[`${base}.tsx`] || allModules[`${base}.ts`];
        } else {
          // Try with ../ prefix first
          const base = `../components/${route.componentPath}`;
          moduleLoader = allModules[`${base}.jsx`] || allModules[`${base}.js`]
            || allModules[`${base}.tsx`] || allModules[`${base}.ts`];

          // If not found, try with ./ (for components in same folder)
          if (!moduleLoader) {
            const localBase = `./${route.componentPath}`;
            moduleLoader = allModules[`${localBase}.jsx`] || allModules[`${localBase}.js`]
              || allModules[`${localBase}.tsx`] || allModules[`${localBase}.ts`];
          }
        }
        
        if (!moduleLoader) {
          console.error(`Dynamic route module not found: ${route.componentPath}`);
          Component = () => <div>Component not found: {route.componentPath}</div>;
        } else {
          const componentLoader = () => {
            return moduleLoader().then(module => {
              switch (route.componentType) {
                case 'named_export':
                  return { default: module[route.exportName] };
                default:
                  return module;
              }
            });
          };
          
          Component = lazy(componentLoader);
        }
      } else {
        // Default to Performance_content_layout for performance forms
        Component = lazy(() => import("./analytics/Performance_content_layout"));
      }

      // Parse props from database
      let routeProps = { title: route.title };
      try {
        if (route.props) {
          const parsedProps = typeof route.props === 'string' ? JSON.parse(route.props) : route.props;
          routeProps = { ...routeProps, ...parsedProps };
        }
        const templatePropsData = route.templateProps || route.default_props;
        if (templatePropsData) {
          const parsedTemplateProps = typeof templatePropsData === 'string' 
            ? JSON.parse(templatePropsData) 
            : templatePropsData;
          routeProps = { ...routeProps, ...parsedTemplateProps };
        }
      } catch (e) {
        console.warn(`Invalid props JSON for route ${route.path}:`, e.message);
      }

      return (
        <Route
          key={route.path}
          path={route.path}
          element={
            <Suspense fallback={<div>Loading...</div>}>
              <Component {...routeProps} />
            </Suspense>
          }
        />
      );
    });
  };

  if (loading || menuData.pages.length === 0) {
    return <div>Loading application...</div>;
  }

  return (
    <div id="menu">
      <div id="background-color-dashboard" className="print-hidden"></div>
      
      {/* Top Bar */}
      <ErrorBoundary_component
        fallback={
          <div className="err_default err_topBar">
            <p><b>Error:</b><i>Top Bar <span className={"errorCode"}>(113)</span></i></p>
          </div>
        }
      >
        <Suspense fallback={<h1>Loading</h1>}>
          <TopBar_2 menuPages={menuData.pages} menuSections={menuSections} />
        </Suspense>
      </ErrorBoundary_component>

      {/* Main Layout */}
      <div id="test-container">
        {/* Side Bar */}
        <ErrorBoundary_component
          fallback={
            <div className="err_default err_sideBar">
              <p><b>Error:</b><i>Side Bar <span className={"errorCode"}>(100)</span></i></p>
            </div>
          }
        >
          <Suspense fallback={<h1>Loading</h1>}>
            <SideBar_2 menuPages={menuData.pages} menuSections={menuSections} />
          </Suspense>
        </ErrorBoundary_component>

        {/* Main Content */}
        <div id="page-container">
          <div id="page-content">
            {bannerVisibility === 1 ? <Banner_Notification /> : null}
            
            <ErrorBoundary_component
              fallback={
                <div className="err_default err_pg">
                  <p><b>Error: </b><i>Page <span className={"errorCode"}>(108)</span></i></p>
                </div>
              }
            >
              <Suspense fallback={<h1>Loading</h1>}>
                <Routes>
                  {/* System routes */}
                  <Route index element={<Home />} />
                  
                  {/* All main routes from database */}
                  {menuData.pages.map(page => renderWithLayout(page))}
                  
                  {/* Template-based routes from database */}
                  {renderDynamicRoutes()}
                  
                  {/* 404 */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </ErrorBoundary_component>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AppRouter;